import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton } from '@/components/ui/daisy';
import ScheduledLesson from '@/models/ScheduledLesson';

import { BookOpenIcon } from '@heroicons/react/24/outline';
import { formatLessonName } from '@/utils/lesson/name';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { Badge, Button, Card } from '@/components/ui/daisy';
import { ROUTES } from '@/utils/routes';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Klass from '@/models/Klass';
import { Tooltip } from '../ui/custom/dataDisplay/Tooltip';

export type SimpleScheduledLessonCardProps = {
  scheduledLesson: ScheduledLesson;
  klass?: Klass;
  isLoading?: boolean;
};

export default function SimpleScheduledLessonCard(
  props: SimpleScheduledLessonCardProps,
) {
  const { lesson, datetime, isActive } = props.scheduledLesson;
  const { klass, isLoading: loading } = props;
  const { t } = useTranslation('translation', { keyPrefix: 'common' });

  const disabledColor = isActive ? '' : 'opacity-20 pointer-events-none';

  const formatedName = formatLessonName(lesson, props.scheduledLesson);

  if (loading)
    return (
      <Skeleton data-testid="loading" className="min-h-21 w-full rounded-lg" />
    );

  if (klass)
    return (
      <Card
        data-testid="scheduledLessonCard"
        className={twMerge(
          'flex items-center rounded-xl gap-2 bg-base-200 p-4',
          disabledColor,
        )}
      >
        <div className="grid grid-cols-3 gap-x-2.5 gap-y-0.5 md:items-center overflow-hidden w-full">
          <div className="col-span-2 flex flex-col gap-y-4 overflow-hidden justify-start">
            <Text
              text={formatedName}
              className="overflow-hidden text-base-content font-semibold"
            />

            <div className="flex gap-x-2">
              <Text text={klass.name} className="text-base-content text-sm" />
              <Badge size="sm" color="neutral" variant="soft">
                {moment(datetime).format('HH:mm')}h
              </Badge>
            </div>
          </div>
          <ConditionalRenderer
            condition={!!lesson}
            fallback={
              <Tooltip text={t('unassignedLesson')} placement="top">
                <Button
                  variant="soft"
                  color="info"
                  className="rounded-lg cursor-not-allowed"
                >
                  <BookOpenIcon className="size-4" />
                </Button>
              </Tooltip>
            }
          >
            <Link
              to={
                lesson
                  ? ROUTES.COURSES.LESSON.INFO(
                      klass.coursePathSlug,
                      lesson.id,
                      klass.id,
                    )
                  : ''
              }
              className="flex items-center justify-end col-span-1"
            >
              <Button variant="soft" color="info" className="rounded-lg">
                <BookOpenIcon className="size-4" />
              </Button>
            </Link>
          </ConditionalRenderer>
        </div>
      </Card>
    );

  return null;
}
